import { useState } from 'react';
import { Filter, Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { EmptyState } from '@/components/shared/EmptyState';
import type { Trace } from '@/features/traceability/type/Tool';
import type { FilterRule, FilterMode, FilterDimension, ChartMode } from '../../type/dynamicChart';
import { filterDimensionRegistry } from '../../utils/filterDimensions';
import { FilterRuleRow } from './FilterRuleRow';
import { FilterDimensionPicker } from './FilterDimensionPicker';

type FilterPanelProps = {
  filters: FilterRule[];
  filterMode: FilterMode;
  traces: (Trace & { toolId: string })[];
  onFiltersChange: (filters: FilterRule[]) => void;
  onFilterModeChange: (mode: FilterMode) => void;
  mode?: ChartMode;
};

export function FilterPanel({
  filters,
  filterMode,
  traces,
  onFiltersChange,
  onFilterModeChange,
  mode,
}: FilterPanelProps) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleAddDimension = (dim: FilterDimension) => {
    const dimDef = filterDimensionRegistry.find((d) => d.key === dim);
    const newRule: FilterRule = {
      id: crypto.randomUUID(),
      dimension: dim,
      operator: dimDef?.operators[0] ?? 'in',
      values: [],
      negate: false,
    };
    onFiltersChange([...filters, newRule]);
  };

  const handleUpdateRule = (updated: FilterRule) => {
    onFiltersChange(filters.map((r) => (r.id === updated.id ? updated : r)));
  };

  const handleRemoveRule = (id: string) => {
    onFiltersChange(filters.filter((r) => r.id !== id));
  };

  const handleClearAll = () => {
    onFiltersChange([]);
  };

  return (
    <div className="space-y-3 border-t border-border p-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {t('analytics.dashboard.complexCharts.filters.title')}
          </span>
          {filters.length > 0 && (
            <Badge tone="primary" size="sm" className="font-mono">
              {filters.length}
            </Badge>
          )}
        </div>

        {/* ALL/ANY toggle */}
        <div className="flex items-center gap-1">
          <SegmentedControl<FilterMode>
            size="sm"
            aria-label={t('analytics.dashboard.complexCharts.filters.modeLabel')}
            value={filterMode}
            onChange={onFilterModeChange}
            options={[
              { value: 'ALL', label: t('analytics.dashboard.complexCharts.filters.allTraces') },
              { value: 'ANY', label: t('analytics.dashboard.complexCharts.filters.anyTrace') },
            ]}
          />

          {filters.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              aria-label={t('analytics.dashboard.complexCharts.filters.clearAll')}
            >
              {t('analytics.dashboard.complexCharts.filters.clearAll')}
            </Button>
          )}
        </div>
      </div>

      {/* Rules list */}
      {filters.length === 0 ? (
        <EmptyState
          size="compact"
          icon={Filter}
          title={t('analytics.dashboard.complexCharts.filters.noFilters')}
          description={t('analytics.dashboard.complexCharts.filters.allData')}
        />
      ) : (
        <div className="space-y-1">
          {filters.map((rule, idx) => (
            <div key={rule.id}>
              {idx > 0 && (
                <div className="flex items-center gap-2 px-2 py-1">
                  <div className="h-px flex-1 bg-border" />
                  <span className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">
                    {filterMode === 'ALL'
                      ? t('analytics.dashboard.complexCharts.filters.allTraces')
                      : t('analytics.dashboard.complexCharts.filters.anyTrace')}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <FilterRuleRow
                rule={rule}
                traces={traces}
                onChange={handleUpdateRule}
                onRemove={() => handleRemoveRule(rule.id)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add filter row */}
      <FilterDimensionPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleAddDimension}
        mode={mode}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={t('analytics.dashboard.complexCharts.filters.addFilter')}
          aria-expanded={pickerOpen}
          className="w-full justify-start"
        >
          <Plus aria-hidden />
          {t('analytics.dashboard.complexCharts.filters.addFilter')}
        </Button>
      </FilterDimensionPicker>
    </div>
  );
}
